import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import { X, CircleAlert } from 'lucide-react';
import { ContactoForm, ContactoErrors } from '../hooks/useNuevoCliente';

interface ContactoEditorProps {
  contacto?: ContactoForm | null;
  errors: ContactoErrors;
  onAgregar: (datos: Omit<ContactoForm, 'id'>) => boolean;
  onEditar: (datos: ContactoForm) => boolean;
  onCancelar: () => void;
}

// SPEC-177 (RN-004) — un único campo "Nombre del contacto" reemplaza Nombres +
// Apellidos. Internamente se sigue emitiendo `nombres` (todo el texto) y
// `apellidos: ''` hacia onAgregar/onEditar para no tocar la forma de ContactoForm.
const FORM_INICIAL = {
  nombreContacto: '',
  cargo: '',
  telefono: '',
  correoElectronico: '',
};

export function ContactoEditor({ contacto, errors, onAgregar, onEditar, onCancelar }: ContactoEditorProps) {
  const [form, setForm] = useState(FORM_INICIAL);

  useEffect(() => {
    if (contacto) {
      // SPEC-177 — un contacto legado (con apellidos ya guardados) se precarga
      // concatenando nombres + apellidos en el único campo, para no perder el dato
      // visualmente al editar.
      setForm({
        nombreContacto: [contacto.nombres, contacto.apellidos].filter(Boolean).join(' ').trim(),
        cargo: contacto.cargo,
        telefono: contacto.telefono,
        correoElectronico: contacto.correoElectronico,
      });
    } else {
      setForm(FORM_INICIAL);
    }
  }, [contacto]);

  const handleChange = (campo: keyof typeof FORM_INICIAL) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  const handleSubmit = () => {
    const datos = {
      nombres: form.nombreContacto,
      apellidos: '',
      cargo: form.cargo,
      telefono: form.telefono,
      correoElectronico: form.correoElectronico,
      esPrincipal: false,
    };
    if (contacto && contacto.id !== 'nuevo') {
      onEditar({ ...datos, id: contacto.id });
    } else {
      onAgregar(datos);
    }
  };

  const esEdicion = contacto && contacto.id !== 'nuevo';

  return (
    <Box
      sx={{
        border: '1.5px dashed',
        borderColor: 'grey.300',
        borderRadius: '8px',
        p: '20px',
        position: 'relative',
        backgroundColor: '#fafafa',
      }}
    >
      {/* Botón cerrar */}
      <IconButton
        size="small"
        onClick={onCancelar}
        aria-label="Cerrar editor de contacto"
        sx={{ position: 'absolute', top: 10, right: 10, color: 'text.secondary' }}
      >
        <X size={20} />
      </IconButton>

      <Typography
        sx={{ fontWeight: 600, fontSize: '0.88rem', mb: 2, fontFamily: 'Poppins, sans-serif', color: 'text.primary' }}
      >
        {esEdicion ? 'Editar contacto' : 'Nuevo contacto'}
      </Typography>

      {/* Grid 2 cols */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
        {/* Nombre del contacto — span 2 */}
        <TextField
          label="Nombre del contacto"
          value={form.nombreContacto}
          onChange={handleChange('nombreContacto')}
          size="small"
          required
          error={!!errors.nombres}
          helperText={
            errors.nombres ? (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircleAlert size={14} />
                {errors.nombres}
              </Box>
            ) : undefined
          }
          inputProps={{ maxLength: 160 }}
          sx={{ ...inputSx, gridColumn: '1 / -1' }}
        />

        {/* Cargo — span 2 */}
        <TextField
          label="Cargo (opcional)"
          value={form.cargo}
          onChange={handleChange('cargo')}
          size="small"
          inputProps={{ maxLength: 100 }}
          sx={{ ...inputSx, gridColumn: '1 / -1' }}
        />

        {/* Teléfono */}
        <TextField
          label="Teléfono"
          value={form.telefono}
          onChange={handleChange('telefono')}
          size="small"
          required
          placeholder="+57 300 000 0000"
          error={!!errors.telefono}
          helperText={
            errors.telefono ? (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircleAlert size={14} />
                {errors.telefono}
              </Box>
            ) : undefined
          }
          inputProps={{ maxLength: 20 }}
          sx={inputSx}
        />

        {/* Correo */}
        <TextField
          label="Correo electrónico"
          value={form.correoElectronico}
          onChange={handleChange('correoElectronico')}
          size="small"
          required
          type="email"
          error={!!errors.correoElectronico}
          helperText={
            errors.correoElectronico ? (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircleAlert size={14} />
                {errors.correoElectronico}
              </Box>
            ) : undefined
          }
          inputProps={{ maxLength: 120 }}
          sx={inputSx}
        />
      </Box>

      {/* Botones */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button
          variant="text"
          onClick={onCancelar}
          sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', color: 'text.secondary' }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            fontFamily: 'Poppins, sans-serif',
            textTransform: 'none',
            backgroundColor: '#963781',
            '&:hover': { backgroundColor: '#7D2D6C' },
            height: 40,
            borderRadius: '6px',
          }}
        >
          {esEdicion ? 'Guardar cambios' : 'Agregar contacto'}
        </Button>
      </Box>
    </Box>
  );
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    fontFamily: 'Poppins, sans-serif',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Poppins, sans-serif',
  },
};
